/**
 * Persist in-progress setup UI (connector / location) across tab switches,
 * focus loss, remounts, and refresh. Never stores provider secrets.
 */

export const SECRET_DRAFT_KEYS = [
  "credential_ref",
  "client_id",
  "client_secret",
  "access_token",
  "refresh_token",
  "password",
  "authorization_code",
  "secret",
  "token",
] as const;

export function isSecretDraftKey(key: string): boolean {
  const k = key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
  if (SECRET_DRAFT_KEYS.includes(k as (typeof SECRET_DRAFT_KEYS)[number])) return true;
  if (k.endsWith("_secret") || k.endsWith("_token") || k.endsWith("_password")) return true;
  return false;
}

function stripSecretDraftValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripSecretDraftValue);
  if (value && typeof value === "object") {
    return stripSecretDraftFields(value as Record<string, unknown>);
  }
  return value;
}

export function stripSecretDraftFields<T extends Record<string, unknown>>(value: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    if (isSecretDraftKey(key)) continue;
    out[key] = stripSecretDraftValue(v);
  }
  return out as T;
}

export function connectorDraftStorageKey(orgId: string | null | undefined): string {
  return `tradivo:setup-draft:connector:${orgId || "unknown"}`;
}

export function locationDraftStorageKey(orgId: string | null | undefined): string {
  return `tradivo:setup-draft:location:${orgId || "unknown"}`;
}

export function readSetupDraft<T>(storage: Storage | undefined, key: string): T | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return stripSecretDraftFields(parsed as T & Record<string, unknown>);
  } catch {
    return null;
  }
}

export function writeSetupDraft(storage: Storage | undefined, key: string, value: Record<string, unknown>): void {
  if (!storage) return;
  try {
    const safe = stripSecretDraftFields(value);
    storage.setItem(key, JSON.stringify(safe));
  } catch {
    // Storage can be disabled or full. Draft persistence is best-effort.
  }
}

export function clearSetupDraft(storage: Storage | undefined, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be disabled. The in-memory draft is still cleared by the hook.
  }
}

export function sessionStorageOrUndefined(): Storage | undefined {
  try {
    return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}
